import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { Config } from '../config';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';
import Toast from '../components/Toast';

export default function ResetPassword() {
  const labels = useLabels();
  const t = useT();
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Verify token on mount
    const verifyToken = async () => {
      try {
        const response = await authApi.verifyResetToken(token);
        if (response.data.valid) {
          setTokenValid(true);
        } else {
          setError(response.data.message || t('auth.invalidOrExpiredToken'));
        }
      } catch (err) {
        setError(err.response?.data?.message || t('auth.invalidOrExpiredToken'));
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setError(t('auth.noResetToken'));
      setVerifying(false);
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      await authApi.resetPassword(token, formData.password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { state: { message: 'Password reset successfully! Please log in with your new password.' } });
      }, 3000);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.message) {
        setError(`${errorDetail.message}: ${errorDetail.reasons?.join(', ')}`);
      } else {
        setError(typeof errorDetail === 'string' ? errorDetail : t('auth.failedResetPassword'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-eucnet-gradient flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-white text-lg">{t('auth.verifyingToken')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-eucnet-gradient flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <Toast
        open={success}
        setOpen={setSuccess}
        type="success"
        className="fixed top-6 right-6 z-50"
      >
        {t('auth.passwordResetRedirecting')}
      </Toast>

      <div className="max-w-md w-full space-y-8">
        <div>
          {/* EU CyberNet Logo */}
          <div className="flex justify-center overflow-hidden max-h-[140px] mb-0">
            <img
              src={labels.loginLogo || Config.loginLogo}
              alt="Logo"
              className="h-[196px] w-auto object-top"
            />
          </div>
          <h2 className="mt-2 text-center text-3xl font-bold text-white tracking-tight">
            {labels.platformTitle}
          </h2>
          <p className="mt-3 text-center text-2xl text-white/90 font-medium">
            {labels.platformSubtitle}
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          {!tokenValid ? (
            <div className="text-center">
              <div className="text-red-600 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-eucnet-navy mb-2">{t('auth.invalidResetLink')}</h2>
              <p className="text-gray-600 mb-6">{error || t('auth.invalidResetLinkDesc')}</p>
              <Link 
                to="/forgot-password" 
                className="inline-block px-6 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy transition-colors"
              >
                {t('auth.requestNewResetLink')}
              </Link>
              <div className="mt-4">
                <Link to="/login" className="text-sm font-medium text-eucnet-blue hover:text-eucnet-sky transition-colors">
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-eucnet-navy mb-2">{t('auth.resetYourPassword')}</h2>
                <p className="text-sm text-gray-600">
                  {t('auth.enterNewPasswordDesc')}
                </p>
              </div>

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-eucnet-navy mb-2">
                    {t('auth.newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      className="block w-full px-4 py-3 pr-10 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                      placeholder={t('auth.enterNewPassword')}
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 z-10"
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-eucnet-navy mb-2">
                    {t('auth.confirmNewPasswordLabel')}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      className="block w-full px-4 py-3 pr-10 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                      placeholder={t('auth.confirmNewPassword')}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 z-10"
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-4 rounded-lg">
                    {error}
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading || success}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-base font-semibold text-white bg-gradient-to-r from-eucnet-primary to-eucnet-sky hover:from-eucnet-navy hover:to-eucnet-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eucnet-sky disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
                  >
                    {loading ? t('auth.resetting') : success ? t('auth.passwordResetDone') : t('auth.resetPassword')}
                  </button>
                </div>

                <div className="text-center">
                  <Link to="/login" className="text-sm font-medium text-eucnet-blue hover:text-eucnet-sky transition-colors">
                    {t('auth.backToLogin')}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm text-white/80 font-medium">
          © 2026 {labels.brandName}. {t('common.allRightsReserved')}
        </p>
      </div>
    </div>
  );
}
